import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Bug, Store, Tag, Folder, Grid3X3, LinkSlash, Network, 
  ChevronDown, ChevronRight, LayoutDashboard, X, Menu
} from 'lucide-react';

const navItems = [
  {
    text: "Home",
    icon: <LayoutDashboard size={20} />,
    to: "/dashboard",
    color: "bg-blue-500",
    hoverColor: "hover:bg-blue-50 hover:text-blue-700"
  },
  {
    text: "Cupons capturados",
    icon: <Bug size={20} />,
    to: "/crawled-coupon",
    color: "bg-green-500",
    hoverColor: "hover:bg-green-50 hover:text-green-700",
    subItems: [
      {
        text: "TODOS",
        to: "/crawled-coupon",
      },
      {
        text: "Novos",
        to: "/crawled-coupon/new",
        badge: "NEW"
      },
      {
        text: "Removidos",
        to: "/crawled-coupon/removed",
      },
      {
        text: "Cupons de Loja",
        to: "/crawled-coupon/store",
      },
    ],
  },
  {
    text: "Lojas",
    icon: <Store size={20} />,
    to: "/stores",
    color: "bg-purple-500",
    hoverColor: "hover:bg-purple-50 hover:text-purple-700"
  },
  {
    text: "Cupons",
    icon: <Tag size={20} />,
    to: "/coupons",
    color: "bg-orange-500",
    hoverColor: "hover:bg-orange-50 hover:text-orange-700"
  },
  {
    text: "Categorias",
    icon: <Folder size={20} />,
    to: "/categories",
    color: "bg-yellow-500",
    hoverColor: "hover:bg-yellow-50 hover:text-yellow-700"
  },
  {
    text: "Especiais",
    icon: <Grid3X3 size={20} />,
    to: "/specials",
    color: "bg-pink-500",
    hoverColor: "hover:bg-pink-50 hover:text-pink-700"
  },
  {
    text: "Redes",
    icon: <Network size={20} />,
    to: "/network",
    color: "bg-indigo-500",
    hoverColor: "hover:bg-indigo-50 hover:text-indigo-700"
  },
];

const Sidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  drawerWidth,
  isMobile,
}) => {
  const [active, setActive] = useState("");
  const [openItems, setOpenItems] = useState({});
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setActive(pathname);
    
    // Auto-expand parent if child is active
    navItems.forEach((item, index) => {
      if (item.subItems) {
        const hasActiveChild = item.subItems.some(subItem => pathname === subItem.to);
        if (hasActiveChild) {
          setOpenItems(prev => ({ ...prev, [index]: true }));
        }
      }
    });
  }, [pathname]);

  const handleClick = (to, index, hasSubItems) => {
    if (hasSubItems) {
      setOpenItems((prevOpenItems) => ({
        ...prevOpenItems,
        [index]: !prevOpenItems[index],
      }));
    } else if (to) {
      setActive(to);
      navigate(to);
      if (isMobile) {
        setIsSidebarOpen(false);
      }
    }
  };

  const closeSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Overlay para mobile
  const renderOverlay = () => {
    if (isMobile && isSidebarOpen) {
      return (
        <div 
          className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm"
          onClick={closeSidebar}
        />
      );
    }
    return null;
  };

  if (!isSidebarOpen && isMobile) {
    return null;
  }

  return (
    <>
      {renderOverlay()}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex flex-col border-r border-gray-200/80 bg-white/95 backdrop-blur-xl pt-16 shadow-xl transition-all duration-300 ease-in-out ${
          isSidebarOpen ? 'w-72' : 'w-0 -translate-x-full'
        }`}
      >
        {/* Header da Sidebar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200/50">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <LayoutDashboard size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Dashboard</h2>
              <p className="text-xs text-gray-500">Gerenciamento</p>
            </div>
          </div>
          
          {isMobile && (
            <button
              onClick={closeSidebar}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            >
              <X size={18} className="text-gray-500" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <nav className="p-4 space-y-2">
            {navItems.map(({ text, icon, to, subItems, color, hoverColor }, index) => {
              const isActive = active.startsWith(to);
              const isExpanded = openItems[index];
              
              return (
                <div key={text} className="relative">
                  <button
                    onClick={() => handleClick(to, index, !!subItems)}
                    className={`group relative flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg shadow-blue-500/25'
                        : `text-gray-700 ${hoverColor} hover:shadow-md`
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg transition-colors ${
                        isActive 
                          ? 'bg-white/20' 
                          : `${color} text-white group-hover:scale-110`
                      }`}>
                        {icon}
                      </div>
                      <span className="font-medium">{text}</span>
                    </div>
                    
                    {subItems && (
                      <div className={`transition-transform duration-200 ${
                        isExpanded ? 'rotate-0' : '-rotate-90'
                      }`}>
                        <ChevronDown size={16} />
                      </div>
                    )}
                    
                    {/* Indicador lateral para item ativo */}
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                    )}
                  </button>
                  
                  {/* Sub-items com animação */}
                  {subItems && (
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isExpanded ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="ml-6 mt-2 space-y-1 border-l-2 border-gray-200/50 pl-4">
                        {subItems.map((subItem) => {
                          const isSubActive = active === subItem.to;
                          
                          return (
                            <button
                              key={subItem.to}
                              onClick={() => handleClick(subItem.to)}
                              className={`group relative flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
                                isSubActive
                                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <span>{subItem.text}</span>
                              
                              {/* Badge para itens especiais */}
                              {subItem.badge && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                                  {subItem.badge}
                                </span>
                              )}
                              
                              {/* Indicador para sub-item ativo */}
                              {isSubActive && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-blue-500 rounded-r-full" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Footer da Sidebar */}
        <div className="p-4 border-t border-gray-200/50">
          <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl">
            <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center">
              <span className="text-xs font-bold text-white">U</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">Usuário</p>
              <p className="text-xs text-gray-500 truncate">Admin</p>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </>
  );
};

export default Sidebar;